<div class="watchdog-logs-container">
    <p-toast></p-toast>

    <!-- Page Header -->
    <div class="page-header">
        <h2>Drupal Watchdog Logs</h2>
        <div class="subtitle">View and search system logs from the Drupal 6 portal</div>
    </div>

    <!-- Filters Section -->
    <div class="filters-section">
        <div class="filter-row">
            <div class="filter-field">
                <label for="search">Search</label>
                <input pInputText
                       id="search"
                       type="text"
                       [(ngModel)]="searchTerm"
                       placeholder="Search message, type, hostname, location..."
                       (keyup.enter)="applyFilters()" />
            </div>

            <div class="filter-field">
                <label for="severity">Severity</label>
                <p-dropdown [options]="severityOptions"
                           [(ngModel)]="selectedSeverity"
                           optionLabel="label"
                           optionValue="value"
                           placeholder="Select severity">
                    <ng-template let-option pTemplate="item">
                        <p-tag *ngIf="option.severity"
                               [severity]="option.severity"
                               [value]="option.label"></p-tag>
                        <span *ngIf="!option.severity">{{ option.label }}</span>
                    </ng-template>
                </p-dropdown>
            </div>

            <div class="filter-field">
                <label for="type">Type</label>
                <p-dropdown [options]="logTypeOptions"
                           [(ngModel)]="selectedType"
                           optionLabel="label"
                           optionValue="value"
                           [filter]="true"
                           filterPlaceholder="Search types..."
                           placeholder="Select type"></p-dropdown>
            </div>

            <div class="filter-field">
                <label for="startDate">Start Date</label>
                <p-calendar [(ngModel)]="startDate"
                           [showTime]="true"
                           [showIcon]="true"
                           dateFormat="mm/dd/yy"
                           placeholder="Select start date"></p-calendar>
            </div>

            <div class="filter-field">
                <label for="endDate">End Date</label>
                <p-calendar [(ngModel)]="endDate"
                           [showTime]="true"
                           [showIcon]="true"
                           dateFormat="mm/dd/yy"
                           placeholder="Select end date"></p-calendar>
            </div>

            <div class="filter-actions">
                <button pButton
                        type="button"
                        label="Apply"
                        icon="pi pi-search"
                        (click)="applyFilters()"
                        class="p-button-primary"></button>
                <button pButton
                        type="button"
                        label="Clear"
                        icon="pi pi-times"
                        (click)="clearFilters()"
                        class="p-button-secondary p-button-outlined"></button>
            </div>
        </div>
    </div>

    <!-- Data Table -->
    <div class="table-container">
        <p-table #logsTable
                 [value]="logs"
                 [paginator]="true"
                 [rows]="rows"
                 [rowsPerPageOptions]="rowsPerPageOptions"
                 [totalRecords]="totalLogs"
                 [loading]="isLoading"
                 [lazy]="true"
                 [showCurrentPageReport]="true"
                 currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                 (onLazyLoad)="getLogs($event)"
                 dataKey="wid"
                 [sortField]="'timestamp'"
                 [sortOrder]="-1"
                 [expandedRowKeys]="expandedRowKeys">

            <!-- Table Header -->
            <ng-template pTemplate="header">
                <tr>
                    <th style="width: 3rem"></th>
                    <th pSortableColumn="timestamp" style="width: 180px">
                        Time
                        <p-sortIcon field="timestamp"></p-sortIcon>
                    </th>
                    <th pSortableColumn="severity" style="width: 100px">
                        Severity
                        <p-sortIcon field="severity"></p-sortIcon>
                    </th>
                    <th pSortableColumn="type" style="width: 150px">
                        Type
                        <p-sortIcon field="type"></p-sortIcon>
                    </th>
                    <th>Message</th>
                    <th pSortableColumn="hostname" style="width: 150px">
                        Hostname
                        <p-sortIcon field="hostname"></p-sortIcon>
                    </th>
                    <th pSortableColumn="uid" style="width: 80px">
                        User ID
                        <p-sortIcon field="uid"></p-sortIcon>
                    </th>
                </tr>
            </ng-template>

            <!-- Table Caption/Toolbar -->
            <ng-template pTemplate="caption">
                <div class="table-header-content">
                    <div class="header-left">
                        <span class="text-color-secondary">
                            <i class="pi pi-info-circle"></i>
                            Total: {{ totalLogs }} logs
                        </span>
                    </div>
                    <div class="header-right">
                        <button pButton
                                type="button"
                                icon="pi pi-refresh"
                                label="Refresh"
                                (click)="refresh()"
                                class="p-button-outlined p-button-sm"></button>
                        <button pButton
                                type="button"
                                icon="pi pi-download"
                                label="Export CSV"
                                (click)="exportLogs()"
                                class="p-button-outlined p-button-sm"></button>
                    </div>
                </div>
            </ng-template>

            <!-- Table Body -->
            <ng-template pTemplate="body" let-log let-expanded="expanded">
                <tr class="expandable-row" (click)="toggleRowExpansion(log)">
                    <td>
                        <button pButton
                                type="button"
                                class="p-button-text p-button-rounded p-button-plain"
                                [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></button>
                    </td>
                    <td>
                        <span [pTooltip]="formatTimestamp(log.timestamp)">
                            {{ formatTimestamp(log.timestamp) }}
                        </span>
                    </td>
                    <td>
                        <p-tag [severity]="getSeverityClass(log.severity)"
                               [value]="log.severityLabel || 'Unknown'"></p-tag>
                    </td>
                    <td>
                        <span class="log-type">{{ log.type }}</span>
                    </td>
                    <td>
                        <div class="log-message" [class.expanded]="expanded">
                            {{ log.messageInterpolated || log.message }}
                        </div>
                    </td>
                    <td>
                        <span class="hostname">{{ log.hostname }}</span>
                    </td>
                    <td>{{ log.uid }}</td>
                </tr>
            </ng-template>

            <!-- Expanded Row Template -->
            <ng-template pTemplate="rowexpansion" let-log>
                <tr class="detail-row">
                    <td colspan="7">
                        <div class="detail-content">
                            <div class="detail-grid">
                                <!-- Basic Information -->
                                <div class="detail-section">
                                    <h4>Basic Information</h4>
                                    <div class="detail-item">
                                        <div class="detail-label">Log ID</div>
                                        <div class="detail-value">#{{ log.wid }}</div>
                                    </div>
                                    <div class="detail-item">
                                        <div class="detail-label">Timestamp</div>
                                        <div class="detail-value">
                                            {{ formatTimestamp(log.timestamp) }}
                                            <span class="text-color-secondary"> ({{ log.timestamp }})</span>
                                        </div>
                                    </div>
                                    <div class="detail-item">
                                        <div class="detail-label">User ID</div>
                                        <div class="detail-value">{{ log.uid || 'Anonymous' }}</div>
                                    </div>
                                    <div class="detail-item" *ngIf="log.link">
                                        <div class="detail-label">Link</div>
                                        <div class="detail-value monospace">
                                            <a [href]="log.link" target="_blank" rel="noopener">{{ log.link }}</a>
                                        </div>
                                    </div>
                                </div>

                                <!-- Location Information -->
                                <div class="detail-section">
                                    <h4>Location Information</h4>
                                    <div class="detail-item">
                                        <div class="detail-label">URL</div>
                                        <div class="detail-value monospace">{{ log.location || 'N/A' }}</div>
                                    </div>
                                    <div class="detail-item">
                                        <div class="detail-label">Referer</div>
                                        <div class="detail-value monospace">{{ log.referer || 'N/A' }}</div>
                                    </div>
                                    <div class="detail-item">
                                        <div class="detail-label">Hostname</div>
                                        <div class="detail-value monospace">{{ log.hostname }}</div>
                                    </div>
                                </div>
                            </div>

                            <!-- Message Details -->
                            <div class="detail-section" style="margin-top: 1.5rem;">
                                <h4>Message Details</h4>
                                <div class="detail-item">
                                    <div class="detail-label">Original Message Template</div>
                                    <div class="detail-value">
                                        <pre>{{ log.message }}</pre>
                                    </div>
                                </div>
                                <div class="detail-item" *ngIf="log.messageInterpolated && log.messageInterpolated !== log.message">
                                    <div class="detail-label">Interpolated Message</div>
                                    <div class="detail-value">
                                        <pre>{{ log.messageInterpolated }}</pre>
                                    </div>
                                </div>
                                <div class="detail-item" *ngIf="log.variablesParsed">
                                    <div class="detail-label">Variables (Deserialized)</div>
                                    <div class="detail-value">
                                        <pre>{{ formatJson(log.variablesParsed) }}</pre>
                                    </div>
                                </div>
                                <div class="detail-item" *ngIf="!log.variablesParsed && log.variables && log.variables !== 'N;'">
                                    <div class="detail-label">Variables (Raw)</div>
                                    <div class="detail-value">
                                        <pre>{{ log.variables }}</pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            </ng-template>

            <!-- Empty Message -->
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="7">
                        <div class="empty-state">
                            <i class="pi pi-inbox"></i>
                            <div class="empty-text">
                                <p><strong>No logs found</strong></p>
                                <p>Try adjusting your filters or search criteria</p>
                            </div>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>